import { useEffect, useState } from 'react'
import { Settings, Zap, Download, Github, Coins, Loader2, Check, Archive } from 'lucide-react'
import { useStore } from '@store/useStore'
import { getCredits } from '@services/stripe'
import { PHASE_INFO } from '@agents/orchestrator'
import { isConfigured as isGithubConfigured, getUser, deploySite } from '@services/github'
import { exportAndDownload } from '@utils/exportZip'

export default function Header({ onSettingsClick }) {
  const { project, files, images, credits, setCredits, addMessage } = useStore()
  const [githubUser, setGithubUser] = useState(null)
  const [githubChecking, setGithubChecking] = useState(true)
  const [deploying, setDeploying] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Sync credits from localStorage on mount
  useEffect(() => {
    setCredits(getCredits())
    const handleStorage = () => setCredits(getCredits())
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  // Check GitHub connection
  useEffect(() => {
    checkGithubConnection()
  }, [])

  const checkGithubConnection = async () => {
    setGithubChecking(true)
    if (isGithubConfigured()) {
      try {
        const user = await getUser()
        setGithubUser(user)
      } catch {
        setGithubUser(null)
      }
    } else {
      setGithubUser(null)
    }
    setGithubChecking(false)
  }

  const fileCount = Object.keys(files).length
  const currentPhase = PHASE_INFO[project.phase]

  // Export as deployable ZIP (uses shared utility)
  const handleExport = async () => {
    if (fileCount === 0) return
    setExporting(true)

    try {
      await exportAndDownload({
        files,
        images,
        domain: project.domain,
      })
    } finally {
      setExporting(false)
    }
  }

  // Deploy to GitHub
  const handleDeploy = async () => {
    if (!githubUser) {
      // Not connected - open settings
      onSettingsClick?.()
      return
    }

    setDeploying(true)
    try {
      const repoName = project.domain.replace(/\./g, '-').toLowerCase()
      const result = await deploySite({
        repoName,
        files,
        onProgress: ({ message }) => {
          addMessage?.({ role: 'assistant', content: message, type: 'info' })
        },
      })

      addMessage?.({
        role: 'assistant',
        content: `**Site déployé !**\n\nURL: ${result.url}\nRepo: ${result.repo}`,
        type: 'success',
      })
    } catch (error) {
      addMessage?.({
        role: 'assistant',
        content: `Erreur de déploiement: ${error.message}`,
        type: 'error',
      })
    } finally {
      setDeploying(false)
    }
  }

  return (
    <header className="h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-lg">EMD Factory</span>
        </div>

        {project.domain && (
          <div className="flex items-center gap-2 ml-4 px-3 py-1 bg-slate-800 rounded-lg">
            <span className="text-slate-400 text-sm">Projet:</span>
            <span className="text-primary-400 font-medium">{project.domain}</span>
          </div>
        )}

        {currentPhase && project.phase !== 'init' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-slate-800/50 rounded-lg">
            <span className="text-slate-400 text-sm">Phase:</span>
            <span className="text-accent-400">{currentPhase.name}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Credits */}
        <button
          onClick={onSettingsClick}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
            credits === 0
              ? 'bg-red-500/20 border border-red-500/50 animate-pulse'
              : 'bg-slate-800 hover:bg-slate-700'
          }`}
          title="Cliquez pour recharger vos crédits"
        >
          <Coins className={`w-4 h-4 ${credits === 0 ? 'text-red-400' : 'text-amber-400'}`} />
          <span className="text-sm">
            <span className={`font-medium ${credits === 0 ? 'text-red-400' : 'text-white'}`}>{credits}</span>
            <span className="text-slate-400 ml-1">crédits</span>
          </span>
        </button>

        {/* File count */}
        {fileCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
            <span className="text-sm text-slate-400">{fileCount} pages</span>
          </div>
        )}

        {/* Export ZIP */}
        {fileCount > 0 && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 rounded-lg transition-colors"
            title="Télécharger le site en ZIP"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Archive className="w-4 h-4" />
            )}
            <span className="text-sm">ZIP</span>
          </button>
        )}

        {/* GitHub deploy */}
        {fileCount > 0 && (
          <button
            onClick={handleDeploy}
            disabled={deploying || githubChecking}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
              githubUser
                ? 'bg-primary-600 hover:bg-primary-700'
                : 'bg-slate-700 hover:bg-slate-600'
            } disabled:opacity-50`}
            title={githubUser ? `Déployer sur GitHub (@${githubUser.login})` : 'Connecter GitHub d\'abord'}
          >
            {deploying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : githubUser ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Github className="w-4 h-4" />
            )}
            <span className="text-sm">
              {deploying ? 'Deploy...' : githubUser ? 'Deploy' : 'GitHub'}
            </span>
            {githubUser && !deploying && (
              <img src={githubUser.avatar_url} alt="" className="w-4 h-4 rounded-full" />
            )}
          </button>
        )}

        {/* Settings */}
        <button
          onClick={onSettingsClick}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <Settings className="w-5 h-5 text-slate-400" />
        </button>
      </div>
    </header>
  )
}
